import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { Box, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { EBackupImage } from '../enums/shared/EBackupImage';
import { ETheme } from '../enums/user/ETheme';
import { getFallbackImageSource } from '../functions/getFallbackImageSource';
import { IUser } from '../interfaces/user/IUser';
import { darkPalette, lightPalette } from '../mui/palettes';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export const PreviewImage = styled('img')(({ theme }) => ({
  width: '100px',
  height: '100px',
  objectFit: 'cover',
}));
interface IProps {
  label?: string;
  file: File | null | FileList;
  multiple?: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  accept?: string;
  fallbackType?: EBackupImage;
}

const FileUpload: FC<IProps> = ({
  label,
  file,
  multiple = false,
  onChange,
  accept = 'image/*',
  fallbackType = EBackupImage.OTHER,
}) => {
  const { t } = useTranslation();
  const [preview, setPreview] = useState<string | null>(null);
  const { theme } = useSelector(
    (state: RootState) => state?.auth?.userData,
  ) as IUser;
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
    if (onChange) {
      onChange(e); // Call the parent-provided onChange handler
    }
  };
  const isDark = theme === ETheme.DARK;

  return (
    <Box>
      {label && (
        <Typography
          variant="subtitle2"
          color={
            theme === ETheme.LIGHT
              ? lightPalette.text.gray
              : darkPalette.text.primary
          }
          sx={{ marginBottom: '10px' }}
        >
          {t(label)}
        </Typography>
      )}
      <Button
        component="label"
        role={undefined}
        variant="outlined"
        className="file-upload"
        sx={{
          padding: preview ? '10px' : '46px',
          borderColor:
            theme === ETheme.LIGHT
              ? lightPalette.primary.light
              : darkPalette.background.light,
        }}
        startIcon={
          preview ? (
            <PreviewImage
              src={preview}
              alt="File preview"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = getFallbackImageSource(fallbackType, isDark);
              }}
            />
          ) : (
            <CloudUploadOutlinedIcon
              sx={{ fontSize: '30px !important', margin: 0 }}
              fontSize={'large'}
            />
          )
        }
      >
        <VisuallyHiddenInput
          type="file"
          accept={accept}
          onChange={handleFileChange}
          multiple={multiple}
        />
      </Button>
    </Box>
  );
};

export default FileUpload;
